import React from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Layout from './components/Layout/Layout';
import Login from './pages/Auth/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import MembersList from './pages/Members/MembersList';
import MemberForm from './pages/Members/MemberForm';
import MemberDetail from './pages/Members/MemberDetail';
import FinanceIncomes from './pages/Finance/Incomes';
import IncomeForm from './pages/Finance/IncomeForm';
import FinanceExpenditures from './pages/Finance/Expenditures';
import ExpenditureForm from './pages/Finance/ExpenditureForm';
import FinanceInvoices from './pages/Finance/Invoices';
import InvoiceForm from './pages/Finance/InvoiceForm';
import FinanceIncomeCategories from './pages/Finance/IncomeCategories';
import FinanceExpenditureCategories from './pages/Finance/ExpenditureCategories';
import AssetsList from './pages/Assets/AssetsList';
import AssetForm from './pages/Assets/AssetForm';
import AssetCategories from './pages/Assets/AssetCategories';
// import BranchesLevels from './pages/Branches/Levels';
import HRPositions from './pages/HR/Positions';
import HRExecutives from './pages/HR/Executives';
import HREvents from './pages/HR/Events';
import EventAttendees from './pages/HR/EventAttendees';
import HRGroups from './pages/HR/Groups';
import HRElections from './pages/HR/Elections';
import HRElectionCandidates from './pages/HR/ElectionCandidates';
import EducationCourses from './pages/Education/Courses';
import EducationExams from './pages/Education/Exams';
import EducationExamCandidates from './pages/Education/ExamCandidates';
import EducationExamMarks from './pages/Education/ExamMarks';
import Communication from './pages/Communication/Communication';
import CommunicationPurchaseHistory from './pages/Communication/PurchaseHistory';
import CommunicationSMSHistory from './pages/Communication/SMSHistory';
import ReportsGeneral from './pages/Reports/GeneralReport';
import ReportsFinancial from './pages/Reports/FinancialReport';
import ReportsMembership from './pages/Reports/MembershipReport';
// import ReportsBranchData from './pages/Reports/BranchData';
import ReportsEventsData from './pages/Reports/EventsData';
import StaffList from './pages/Staff/StaffList';
import StaffForm from './pages/Staff/StaffForm';
import AuditLog from './pages/Admin/AuditLog';
import AdminVerifyMember from './pages/Admin/VerifyMember';
import AdminProfile from './pages/Admin/AdminProfile';
import MemberLogin from './pages/MemberPortal/Login';
import MemberForgotPassword from './pages/MemberPortal/ForgotPassword';
import MemberResetPassword from './pages/MemberPortal/ResetPassword';
import MemberRegister from './pages/MemberPortal/Register';
import MemberDashboard from './pages/MemberPortal/Dashboard';
import MemberProfile from './pages/MemberPortal/Profile';
import MemberEditProfile from './pages/MemberPortal/EditProfile';
import MemberEvents from './pages/MemberPortal/Events';
import MemberAnnouncements from './pages/MemberPortal/Announcements';
import MemberVoting from './pages/MemberPortal/Voting';
import MemberExecutives from './pages/MemberPortal/Executives';
import MemberPay from './pages/MemberPortal/Pay';
// import MemberReceipts from './pages/MemberPortal/Receipts';
import MemberIdCard from './pages/MemberPortal/IdCard';
import MemberPortalLayout from './components/MemberPortal/MemberPortalLayout';
import Home from './pages/Home';
import VerifyMember from './pages/VerifyMember';
import KioskVoting from './pages/Kiosk/KioskVoting';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// ATUTEIN redesign tokens — ink-green primary, gold accent for live/focus only
const theme = createTheme({
  palette: {
    primary: {
      main: '#1E5C46',
      light: '#E7F0EC',
      dark: '#174A38',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#D9A441',
      light: '#FBF3E2',
      dark: '#8A6415',
      contrastText: '#14261F',
    },
    background: {
      default: '#F6F8F6',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#14261F',
      secondary: '#66736C',
    },
    divider: '#E3E8E3',
    error: {
      main: '#B4442C',
      light: '#FBEDE9',
    },
    success: {
      main: '#1E5C46',
    },
  },
  typography: {
    fontFamily: '"Inter", "Inter var", sans-serif',
    fontSize: 14,
    h1: { fontFamily: '"Archivo", sans-serif', letterSpacing: '-0.01em' },
    h2: { fontFamily: '"Archivo", sans-serif', letterSpacing: '-0.01em' },
    h3: { fontFamily: '"Archivo", sans-serif', letterSpacing: '-0.01em' },
    h4: { fontFamily: '"Archivo", sans-serif', letterSpacing: '-0.01em' },
    h5: { fontFamily: '"Archivo", sans-serif', letterSpacing: '-0.01em' },
    h6: { fontFamily: '"Archivo", sans-serif', letterSpacing: '-0.01em' },
  },
  shape: {
    borderRadius: 10,
  },
  shadows: [
    'none',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
    '0 20px 50px rgba(20,38,31,.25)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
        },
        outlined: {
          borderColor: '#E3E8E3',
          color: '#14261F',
          '&:hover': {
            borderColor: '#E3E8E3',
            backgroundColor: '#F6F8F6',
          },
        },
        containedPrimary: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none', backgroundColor: '#174A38' },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          border: '1px solid #E3E8E3',
          boxShadow: '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
          borderRadius: 10,
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: '1px solid #E3E8E3',
          boxShadow: '0 1px 2px rgba(20,38,31,.06), 0 4px 14px rgba(20,38,31,.05)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#FBFCFB',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: '#E3E8E3',
          fontSize: 13.5,
        },
        head: {
          fontSize: 11.5,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: '#66736C',
          backgroundColor: '#FBFCFB',
          borderBottom: '1px solid #E3E8E3',
          py: 1.25,
        },
        body: {
          color: '#14261F',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': {
            backgroundColor: '#F4F7F5',
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: '#66736C',
          borderTop: '1px solid #E3E8E3',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#FFFFFF',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E3E8E3' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1E5C46' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1E5C46' },
          '&.Mui-focused': {
            boxShadow: '0 0 0 3px #E7F0EC',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0 20px 50px rgba(20,38,31,.25)',
          border: '1px solid #E3E8E3',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: '"Archivo", sans-serif',
          fontWeight: 600,
          fontSize: 16,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 99,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 9,
        },
        standardError: {
          backgroundColor: '#FBEDE9',
          color: '#7C2E1D',
          border: '1px solid #EFCFC5',
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: '#1E5C46',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F6F8F6',
        },
        '.num, .tabular-nums, td.MuiTableCell-root': {
          fontVariantNumeric: 'tabular-nums',
        },
      },
    },
  },
});

function ElectionIdRedirect() {
  const { electionId } = useParams();
  return <Navigate to={`/dashboard/hr/elections/${electionId}/candidates`} replace />;
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  // Wait for auth check to complete before redirecting
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
}

function MemberProtectedRoute({ children }) {
  const token = localStorage.getItem('memberToken');
  return token ? children : <Navigate to="/member-portal/login" />;
}

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();
  
  // Show loading while checking authentication
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Routes>
      {/* Root and home: show landing page first, not admin login */}
      <Route 
        path="/" 
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Home />} 
      />
      <Route path="/home" element={<Home />} />
      <Route path="/verify/:id" element={<VerifyMember />} />
      <Route path="/kiosk" element={<KioskVoting />} />
      <Route path="/login" element={<Login />} />
      
      {/* Member Portal Routes */}
      <Route path="/member-portal/register" element={<MemberRegister />} />
      <Route path="/member-portal/login" element={<MemberLogin />} />
      <Route path="/member-portal/forgot-password" element={<MemberForgotPassword />} />
      <Route path="/member-portal/reset-password" element={<MemberResetPassword />} />
      <Route
        path="/member-portal"
        element={
          <MemberProtectedRoute>
            <MemberPortalLayout />
          </MemberProtectedRoute>
        }
      >
        <Route index element={<MemberDashboard />} />
        <Route path="events" element={<MemberEvents />} />
        <Route path="events/:eventId" element={<MemberEvents />} />
        <Route path="announcements" element={<MemberAnnouncements />} />
        <Route path="elections" element={<Navigate to="/member-portal" replace />} />
        <Route path="elections/:electionId/vote" element={<MemberVoting />} />
        <Route path="executives" element={<MemberExecutives />} />
        <Route path="pay" element={<MemberPay />} />
        {/* <Route path="receipts" element={<MemberReceipts />} /> */}
        <Route path="card" element={<MemberIdCard />} />
        <Route path="profile" element={<MemberProfile />} />
        <Route path="profile/edit" element={<MemberEditProfile />} />
      </Route>
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="profile" element={<AdminProfile />} />
        
        {/* Members */}
        <Route path="verify-member" element={<AdminVerifyMember />} />
        <Route path="members" element={<MembersList />} />
        <Route path="members/new" element={<MemberForm />} />
        <Route path="members/:id" element={<MemberForm />} />
        <Route path="members/details/:id" element={<MemberDetail />} />
        
        {/* Finance */}
        <Route path="income" element={<FinanceIncomes />} />
        <Route path="income/new" element={<IncomeForm />} />
        <Route path="income/:id" element={<IncomeForm />} />
        <Route path="expenditure" element={<FinanceExpenditures />} />
        <Route path="expenditure/new" element={<ExpenditureForm />} />
        <Route path="expenditure/:id" element={<ExpenditureForm />} />
        <Route path="invoices" element={<FinanceInvoices />} />
        <Route path="invoices/new" element={<InvoiceForm />} />
        <Route path="invoices/:id" element={<InvoiceForm />} />
        <Route path="income-categories" element={<FinanceIncomeCategories />} />
        <Route path="expenditure-categories" element={<FinanceExpenditureCategories />} />
        
        {/* Assets */}
        <Route path="asset" element={<AssetsList />} />
        <Route path="asset/new" element={<AssetForm />} />
        <Route path="asset/:id" element={<AssetForm />} />
        <Route path="asset-categories" element={<AssetCategories />} />
        
        {/* Branches - commented out */}
        {/* <Route path="levels" element={<BranchesLevels />} /> */}
        
        {/* HR */}
        <Route path="hr" element={<Navigate to="/dashboard/hr/elections" replace />} />
        <Route path="hr/elections/:electionId" element={<ElectionIdRedirect />} />
        <Route path="hr/elections" element={<HRElections />} />
        <Route path="hr/elections/:electionId/candidates" element={<HRElectionCandidates />} />
        <Route path="positions" element={<HRPositions />} />
        <Route path="positions/new" element={<HRPositions />} />
        <Route path="executives" element={<HRExecutives />} />
        <Route path="events" element={<HREvents />} />
        <Route path="events/new" element={<HREvents />} />
        <Route path="events/:eventId/attendees" element={<EventAttendees />} />
        <Route path="group" element={<HRGroups />} />
        <Route path="staff" element={<StaffList />} />
        <Route path="staff/new" element={<StaffForm />} />
        <Route path="staff/:id" element={<StaffForm />} />
        <Route path="audit" element={<AuditLog />} />
        
        {/* Education */}
        <Route path="courses" element={<EducationCourses />} />
        <Route path="courses/new" element={<EducationCourses />} />
        <Route path="exams" element={<EducationExams />} />
        <Route path="exams/new" element={<EducationExams />} />
        <Route path="exam-candidates" element={<EducationExamCandidates />} />
        <Route path="exam-candidates/new" element={<EducationExamCandidates />} />
        <Route path="exam-marks" element={<EducationExamMarks />} />
        <Route path="course-categories" element={<EducationCourses />} />
        <Route path="course-types" element={<EducationCourses />} />
        
        {/* Communication */}
        <Route path="communication" element={<Communication />} />
        <Route path="communication/purchaseHistory" element={<CommunicationPurchaseHistory />} />
        <Route path="communication/smsHistory" element={<CommunicationSMSHistory />} />
        
        {/* Reports */}
        <Route path="general-report" element={<ReportsGeneral />} />
        <Route path="financial-report" element={<ReportsFinancial />} />
        <Route path="membership-report" element={<ReportsMembership />} />
        {/* <Route path="reports/branch-data" element={<ReportsBranchData />} /> */}
        <Route path="reports/events-data" element={<ReportsEventsData />} />
        
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
